import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Image as ImageIcon, Trash2 } from 'lucide-react';
import { HighlightItem } from '../types/highlight.types';
import { useTranslation } from '@/shared/i18n';

interface SortableItemProps {
  item: HighlightItem;
  onRemove: (itemId: string) => void;
  allowRemove?: boolean;
  isRemoving?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, onRemove, allowRemove = true, isRemoving = false }) => {
  const { t } = useTranslation('highlights');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 p-4 bg-[#333333] border border-white/10 rounded-lg shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Handle para drag */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-white/40 hover:text-white/60"
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Imagem */}
      <div className="flex-shrink-0">
        {item.cover ? (
          <img
            src={item.cover}
            alt={item.name}
            className="w-16 h-16 object-cover rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`w-16 h-16 bg-[#444444] rounded-lg flex items-center justify-center ${item.cover ? 'hidden' : ''}`}>
          <ImageIcon className="h-8 w-8 text-white/40" />
        </div>
      </div>

      {/* Informações do item */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-white truncate">
          {item.name}
        </h3>
        <p className="text-xs text-white/50">
          ID: {item.id}
        </p>
      </div>

      {/* Ordem e Botão de Remover */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#D91657] text-white">
          {t('order')}: {item.order}
        </span>
        {allowRemove && (
          <button
            onClick={() => onRemove(item.id)}
            disabled={isRemoving}
            className="p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors disabled:opacity-50"
            title={t('removeItem')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

interface HighlightItemListProps {
  items: HighlightItem[];
  onReorder: (startIndex: number, endIndex: number) => void;
  onRemove: (itemId: string) => void;
  allowRemove?: boolean;
  isLoading?: boolean;
  isRemoving?: boolean;
}

export const HighlightItemList: React.FC<HighlightItemListProps> = ({
  items,
  onReorder,
  onRemove,
  allowRemove = true,
  isLoading = false,
  isRemoving = false,
}) => {
  const { t } = useTranslation('highlights');
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      onReorder(oldIndex, newIndex);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 p-4 bg-[#333333] rounded-lg animate-pulse"
          >
            <div className="w-5 h-5 bg-white/10 rounded"></div>
            <div className="w-16 h-16 bg-white/10 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-white/10 rounded w-1/2"></div>
            </div>
            <div className="w-20 h-6 bg-white/10 rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-white/40 mb-4">
          <ImageIcon className="h-12 w-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">
          {t('noItemsInSectionMessage')}
        </h3>
        <p className="text-white/60">
          {t('startByAddingItems')}
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onRemove={onRemove}
              allowRemove={allowRemove}
              isRemoving={isRemoving}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
